<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';
    import { setScrollId } from '$lib/utils/setScrollId';

    import { onMount } from 'svelte';

    import { LONGPRESS_TIMEOUT } from '../_utils/constants';
    import { getWidthByScreenSize } from '../_utils/getWidthByScreenSize';
    import createSliderController from '../_utils/sliderLogic';
    import Limiter from '../_wrappers/Limiter.svelte';
    import Transition from '../_wrappers/Transition.svelte';
    import TextOnImage from '../Image/TextOnImage.svelte';

    import type { Layout } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { Testimonial } from '$lib/types/Testimonial';
    import type { TransitionEffect } from '$lib/types/Theme';

    export let id = '';
    export let bgColor = 'transparent';
    export let testimonials: Testimonial[] = [];
    export let framed = false;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let layout: Layout = DEFAULT_LAYOUT;
    export let language: Language = 'en';

    let offset = 0;
    let pointerDownStartTime = 0;
    let paused = false;
    const isInColumn = layout.isInColumn;

    const validTestimonials = testimonials.filter(
        (testimonial) =>
            typeof testimonial?.headline === 'string' &&
            typeof testimonial?.subHeadline === 'string' &&
            testimonial?.image &&
            testimonial?.mode,
    );

    const { start, next, back, pause, resume, ...sliderController } = createSliderController(
        validTestimonials,
        8000,
    );

    sliderController.offset.subscribe((newOffset: number) => {
        offset = newOffset;
    });

    sliderController.paused.subscribe((newPaused) => {
        paused = newPaused;
    });

    const wrapperClasses = cn(
        'fun-relative fun-isolate fun-w-full fun-overflow-hidden fun-border-black/10 sm:fun-border',
        getBorderRadius('sliderImage'),
        {
            'max-sm:fun-rounded-none': !framed && !isInColumn,
            'fun-border': framed || isInColumn,
        },
    );

    const handlePointerDown = () => {
        pointerDownStartTime = new Date().getTime();
        pause();
    };

    const handlePointerUp = (onClickCb: () => void) => () => {
        const pointerDownDuration = new Date().getTime() - pointerDownStartTime;

        if (pointerDownDuration > LONGPRESS_TIMEOUT) {
            resume();
        } else {
            onClickCb();
        }
    };

    const handlePointerLeave = () => {
        resume();
    };

    onMount(() => {
        start();
    });
</script>

{#if validTestimonials.length > 0}
    <div
        {...setScrollId(id)}
        class="fun-h-full fun-w-full"
        style="fun-transform: translateZ(0); background-color: {isInColumn ? undefined : bgColor}"
    >
        <Limiter>
            {#if validTestimonials.length === 1}
                <div
                    class={framed && !isInColumn
                        ? 'fun-p-6 sm:fun-px-12 sm:fun-py-6 md:fun-px-20 md:fun-py-10'
                        : 'fun-p-0'}
                >
                    <div class={wrapperClasses}>
                        <TextOnImage
                            image={validTestimonials[0].image}
                            headline={validTestimonials[0].headline}
                            subHeadline={validTestimonials[0].subHeadline}
                            mode={validTestimonials[0].mode}
                            fixedMaxWidth={getWidthByScreenSize()}
                            isInMultiColumn={layout.isInMultiColumn}
                        />
                    </div>
                </div>
            {:else}
                <Transition delay={transitionDelay} effect={transitionEffect}>
                    <div
                        style="-webkit-user-select: none; -webkit-touch-callout: none;"
                        class={framed && !isInColumn
                            ? 'fun-p-6 sm:fun-px-12 sm:fun-py-6 md:fun-px-20 md:fun-py-10'
                            : 'fun-p-0'}
                    >
                        <div class={wrapperClasses}>
                            <button
                                aria-label={language === 'de'
                                    ? 'Vorheriges Testimonial'
                                    : 'Previous testimonial'}
                                on:pointerdown={handlePointerDown}
                                on:pointerup={handlePointerUp(back)}
                                on:pointerleave={handlePointerLeave}
                                class="fun-absolute fun-bottom-0 fun-left-0 fun-top-0 fun-z-30 fun-w-1/2 fun-cursor-pointer"
                            />
                            <button
                                aria-label={language === 'de'
                                    ? 'Nächstes Testimonial'
                                    : 'Next testimonial'}
                                on:pointerdown={handlePointerDown}
                                on:pointerup={handlePointerUp(next)}
                                on:pointerleave={handlePointerLeave}
                                class="fun-absolute fun-bottom-0 fun-right-0 fun-top-0 fun-z-30 fun-w-1/2 fun-cursor-pointer"
                            />
                            <div
                                class="fun-absolute fun-bottom-3 fun-left-0 fun-right-0 fun-z-20 fun-flex fun-w-full fun-justify-center fun-space-x-1 fun-px-3"
                            >
                                {#each validTestimonials as testimonial, index (`${testimonial.headline}-${index}`)}
                                    <div
                                        class:fun-bg-white={validTestimonials.indexOf(testimonial) <
                                            offset && validTestimonials[offset].mode === 'dark'}
                                        class:fun-bg-gray-400={validTestimonials.indexOf(
                                            testimonial,
                                        ) >= offset && validTestimonials[offset].mode === 'dark'}
                                        class:fun-bg-gray-300={validTestimonials.indexOf(
                                            testimonial,
                                        ) < offset && validTestimonials[offset].mode === 'light'}
                                        class:fun-bg-gray-200={validTestimonials.indexOf(
                                            testimonial,
                                        ) >= offset && validTestimonials[offset].mode === 'light'}
                                        class="fun-h-1 fun-w-full fun-max-w-[70px] fun-flex-grow fun-overflow-hidden fun-rounded-sm fun-transition"
                                    >
                                        <div
                                            style="animation-fill-mode: forwards"
                                            class:fun-bg-white={testimonial.mode === 'dark'}
                                            class:fun-bg-gray-300={testimonial.mode === 'light'}
                                            class="fun-h-full fun-w-0"
                                            class:fun-animate-fill-slow={validTestimonials.indexOf(
                                                testimonial,
                                            ) === offset}
                                            class:fun-pause={paused}
                                        />
                                    </div>
                                {/each}
                            </div>
                            <div
                                class="fun-flex fun-transition fun-duration-700"
                                style="width: calc({validTestimonials.length} * 100%); transform: translateX(calc({offset} * (-100% / {validTestimonials.length})))"
                            >
                                {#each validTestimonials as testimonial, index (`${testimonial.headline}-loop-${index}`)}
                                    <TextOnImage
                                        image={testimonial.image}
                                        headline={testimonial.headline}
                                        subHeadline={testimonial.subHeadline}
                                        mode={testimonial.mode}
                                        fixedMaxWidth={getWidthByScreenSize()}
                                        isInMultiColumn={layout.isInMultiColumn}
                                    />
                                {/each}
                            </div>
                        </div>
                    </div>
                </Transition>
            {/if}
        </Limiter>
    </div>
{:else}
    <div class="fun-hidden">No valid testimonial slides</div>
{/if}
